import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authApi } from "@/lib/api";
import { PublicHeader, PublicFooter, ChapterBadge } from "@/components/PublicChrome";
import { Compass, LogIn, ShieldAlert } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await authApi.login(email, password);
      sessionStorage.setItem("access_token", res.access_token);
      sessionStorage.setItem("refresh_token", res.refresh_token);
      sessionStorage.setItem("user", JSON.stringify(res.user));
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col" data-testid="auth-page">
      <PublicHeader current="auth" />

      <main className="flex-1 flex items-center justify-center px-6 py-16 relative overflow-hidden">
        {/* Décor : compass filigrane derrière la card */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600'><g fill='none' stroke='%23C8922A' stroke-width='0.6'><circle cx='300' cy='300' r='240'/><circle cx='300' cy='300' r='180'/><circle cx='300' cy='300' r='120'/><path d='M300 60 L315 300 L300 540 L285 300 Z' fill='%23C8922A' fill-opacity='0.4'/><path d='M60 300 L300 285 L540 300 L300 315 Z' fill='%23C8922A' fill-opacity='0.4'/></g></svg>\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "600px auto",
          }}
        />

        <div className="relative w-full max-w-md">
          {/* Card centrale */}
          <div className="border border-or/25 bg-noir-50/60 backdrop-blur-sm rounded-sm p-8 md:p-10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-5">
                <ChapterBadge testId="auth-chapter">
                  Chapitre pro · Le carnet
                </ChapterBadge>
              </div>

              <h1 className="font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight">
                Ouvrir le
                <br />
                <span className="italic text-or">carnet des opérateurs</span>
              </h1>
              <p className="mt-4 text-sm text-parchemin/60 max-w-xs mx-auto">
                Accès réservé aux hôtes, animateurs et administrateurs
                Picnic-Game.
              </p>
            </div>

            <form onSubmit={handleLogin} className="flex flex-col gap-5" data-testid="auth-form">
              {error && (
                <div
                  className="flex items-start gap-2 text-sm py-3 px-4 rounded-sm bg-oxyde/10 border border-oxyde/40 text-parchemin/90"
                  data-testid="auth-error"
                >
                  <ShieldAlert className="w-4 h-4 mt-0.5 text-oxyde flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Field
                label="Email"
                testid="auth-email-wrap"
              >
                <input
                  data-testid="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-3.5 py-3 rounded-sm bg-noir-100 border border-or/20 text-parchemin text-sm outline-none focus:border-or focus:ring-1 focus:ring-or/30 transition"
                />
              </Field>

              <Field label="Mot de passe" testid="auth-password-wrap">
                <input
                  data-testid="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-3.5 py-3 rounded-sm bg-noir-100 border border-or/20 text-parchemin text-sm outline-none focus:border-or focus:ring-1 focus:ring-or/30 transition"
                />
              </Field>

              <button
                data-testid="auth-submit"
                type="submit"
                disabled={loading}
                className={`group mt-2 inline-flex items-center justify-center gap-2 py-3.5 rounded-sm font-medium text-sm tracking-wide transition-all duration-300 ${
                  loading
                    ? "bg-or/40 text-noir/60 cursor-not-allowed"
                    : "bg-or hover:bg-or-100 text-noir shadow-[0_10px_30px_-10px_rgba(200,146,42,0.5)]"
                }`}
              >
                {loading ? (
                  "Vérification…"
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Franchir le seuil
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-or/15 divider-ornament font-display italic text-or/70 text-xs">
              <span aria-hidden="true">✦</span>
              <span>Lot &amp; Célé · 5 villes</span>
              <span aria-hidden="true">✦</span>
            </div>
          </div>

          <div className="mt-6 text-center text-[11px] flex justify-center gap-3 text-parchemin/40">
            <Link
              to="/legal/mentions-legales"
              className="hover:text-or transition-colors"
              data-testid="auth-link-mentions"
            >
              Mentions légales
            </Link>
            <span>·</span>
            <Link
              to="/legal/cgv"
              className="hover:text-or transition-colors"
              data-testid="auth-link-cgv"
            >
              CGV
            </Link>
            <span>·</span>
            <Link
              to="/legal/confidentialite"
              className="hover:text-or transition-colors"
              data-testid="auth-link-rgpd"
            >
              Confidentialité
            </Link>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
        {label}
      </label>
      {children}
    </div>
  );
}
